import _ from 'lodash'
import { z } from 'zod'
import { propagatedFailureSchema } from '../utils/error-failure.js'

// The implemented entity inventory from the System Events design.
export const systemEventEntityTypeSchema = z.enum([
  'ACTION',
  'ACTION_STEP',
  'BUTTON',
  'CHART',
  'COMMENT',
  'FORM',
  'FORM_INPUT',
  'INTEGRATION',
  'MEMBERSHIP',
  'OBJECT_SYNC',
  'ORGANIZATION',
  'ROLE',
  'TABLE',
  'TABLE_CELL',
  'TABLE_COLUMN',
  'TABLE_ROW',
  'TABLE_TAB',
  'TRIGGER',
  'USER',
  'WEBHOOK',
  'WORKFLOW',
  'WORKFLOW_V2',
])

export type SystemEventEntityType = z.infer<typeof systemEventEntityTypeSchema>

/** A table cell has no ID of its own, so its entity ID is the synthetic `<rowId>:<columnId>`. */
export const systemEventCellId = (rowId: string, columnId: string): string => `${rowId}:${columnId}`

const uuidSchema = z.uuid()

export const systemEventCellIdSchema = z.string().refine((id) => {
  const parts = id.split(':')
  return parts.length === 2 && parts.every((part) => uuidSchema.safeParse(part).success)
}, 'Expected <rowId>:<columnId>')

export const systemEventOriginatorTypeSchema = z.enum([
  'FREQUENCY_TRIGGER',
  'NO_CONTEXT',
  // Platform work on behalf of several originators, e.g. a formula recompute serving their requests.
  'SYSTEM',
  'USER',
  'WEBHOOK',
])
export type SystemEventOriginatorType = z.infer<typeof systemEventOriginatorTypeSchema>

export const ANONYMOUS_USER = 'ANONYMOUS'
/** The originator of an event recorded where no hop had established who set the work off. */
export const NO_CONTEXT_ORIGINATOR = 'NO_CONTEXT'

export const systemEventAgentSchema = z.enum(['DOSSBOT', 'ROBOPMO'])
export type SystemEventAgent = z.infer<typeof systemEventAgentSchema>

// The hop that carried the action out: a person's session, a workflow run, or an execution the
// platform runs on its own, such as a formula recompute. Null means the hop is unknown.
export const systemEventSubstrateSchema = z.enum(['UI', 'WORKFLOW', 'SYSTEM'])
export type SystemEventSubstrate = z.infer<typeof systemEventSubstrateSchema>

export const systemEventStatusSchema = z.enum(['SUCCESS', 'FAIL'])
export type SystemEventStatus = z.infer<typeof systemEventStatusSchema>

export const systemEventContainerTypeSchema = z.enum(['WORKFLOW_V1', 'WORKFLOW_V2', 'OBJECT_SYNC'])

/** The automation currently doing the work, independent of its initiator and execution ID. */
export const systemEventContainerSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal(systemEventContainerTypeSchema.enum.WORKFLOW_V1),
    id: z.uuid(),
    actionStepId: z.uuid().nullable(),
  }),
  z.strictObject({
    type: z.literal(systemEventContainerTypeSchema.enum.WORKFLOW_V2),
    id: z.uuid(),
    // A stable grouping key; null until one is available.
    callSiteKey: z.string().min(1).max(512).nullable(),
  }),
  z.strictObject({
    type: z.literal(systemEventContainerTypeSchema.enum.OBJECT_SYNC),
    id: z.uuid(),
  }),
])
export type SystemEventContainer = z.infer<typeof systemEventContainerSchema>

export const systemEventAttributionSchema = z.strictObject({
  originatorType: systemEventOriginatorTypeSchema,
  originatorId: z.string().min(1),
  originatorName: z.string().min(1),
  impersonatedUserId: z.string().min(1).nullable(),
  impersonatedUserName: z.string().min(1).nullable(),
  agentId: z.string().min(1).nullable(),
  substrate: systemEventSubstrateSchema.nullable(),
})
export type SystemEventAttribution = z.infer<typeof systemEventAttributionSchema>

/** Common fields on a completed event. Producers supply these; schemas never mint IDs or time. */
export const systemEventEnvelopeSchema = z.strictObject({
  id: z.uuidv7(),
  previousId: z.uuidv7().nullable(),
  parentId: z.uuidv7().nullable(),
  traceId: z.string().min(1),
  // UTC ISO text with millisecond precision round-trips without changing the deduplication key.
  timestamp: z.iso.datetime({ precision: 3 }),
  orgId: z.uuid(),
  ...systemEventAttributionSchema.shape,
  executionId: z.string().min(1).nullable(),
})

export type SystemEventEnvelope = z.infer<typeof systemEventEnvelopeSchema>

// Only normalized failure information belongs in event payloads, not arbitrary thrown objects.
export const systemEventFailureDataSchema = z.strictObject({
  // Producers supply a sanitized message; validation never copies raw thrown objects.
  failure: propagatedFailureSchema,
})

export const systemEventChangeSchema = z.strictObject({
  before: z.json(),
  after: z.json(),
})
export type SystemEventChange = z.infer<typeof systemEventChangeSchema>

/** Selected, JSON-compatible changes captured at the operation boundary. */
export const systemEventChangesSchema = z
  .record(z.string().min(1), systemEventChangeSchema)
  .refine((changes) => Object.keys(changes).length > 0, 'At least one change is required')

export const systemEventChangedFieldsSchema = z.array(z.string().min(1)).min(1)

/**
 * The fields whose values differ between two snapshots, or undefined when none do.
 * Throws if a changed value isn't JSON, so call it inside a capture callback.
 */
export const systemEventChanges = <TField extends string>(
  before: Record<TField, unknown>,
  after: Record<TField, unknown>,
  fields: readonly TField[],
): Record<string, SystemEventChange> | undefined => {
  const changed = fields.filter((field) => !_.isEqual(before[field], after[field]))
  return changed.length === 0
    ? undefined
    : Object.fromEntries(
        changed.map((field) => [
          field,
          systemEventChangeSchema.parse({ before: before[field], after: after[field] }),
        ]),
      )
}
